import React from 'react';
import MetricDistributionCard from './MetricDistributionCard';

interface SinceraData {
  name: string;
  avg_ads_to_content_ratio: number;
  avg_ad_refresh: number;
  avg_ads_in_view: number;
  avg_page_weight: number;
  avg_cpu: number;
  total_supply_paths: number;
  reseller_count: number | null;
  total_unique_gpids: number;
  id_absorption_rate: number;
}

interface BenchmarkDashboardProps {
  sinceraData: SinceraData;
}

const BenchmarkDashboard: React.FC<BenchmarkDashboardProps> = ({ sinceraData }) => {
  // Generate normal distribution data (mock for now)
  const generateNormalDistribution = (mean: number, stdDev: number, min: number, max: number) => {
    const points: Array<{ x: number; y: number }> = [];
    const numPoints = 50;
    
    for (let i = 0; i <= numPoints; i++) {
      const x = min + (max - min) * (i / numPoints);
      const normalizedX = (x - mean) / stdDev;
      const y = Math.exp(-0.5 * normalizedX * normalizedX) / (stdDev * Math.sqrt(2 * Math.PI));
      points.push({ x, y: y / 4 }); // Normalize for display
    }
    
    return points;
  };

  // Calculate percentile position in normal distribution
  const calculatePercentile = (value: number, mean: number, stdDev: number) => {
    const z = (value - mean) / stdDev;
    // Approximate normal CDF
    const t = 1 / (1 + 0.2316419 * Math.abs(z));
    const d = 0.3989423 * Math.exp(-z * z / 2);
    let p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    if (z > 0) p = 1 - p;
    return Math.round(p * 100);
  };

  const metricsData = [
    {
      name: 'Ads to Content Ratio',
      description: 'Percentage of page content dedicated to advertising',
      value: sinceraData.avg_ads_to_content_ratio ? parseFloat((sinceraData.avg_ads_to_content_ratio * 100).toFixed(1)) : 0,
      unit: '%',
      optimalRange: { min: 14, max: 25 },
      distributionData: generateNormalDistribution(8.5, 5.2, 0, 50),
      benchmarkStats: {
        min: 3.9,
        max: 41.4,
        average: 8.5,
        percentile: calculatePercentile(sinceraData.avg_ads_to_content_ratio ? sinceraData.avg_ads_to_content_ratio * 100 : 0, 8.5, 5.2),
      },
    },
    {
      name: 'Average Refresh Rate',
      description: 'How often ads refresh on the page (lower is better for UX)',
      value: sinceraData.avg_ad_refresh,
      unit: 'sec',
      optimalRange: { min: 45, max: 120 },
      distributionData: generateNormalDistribution(65, 25, 0, 160),
      benchmarkStats: {
        min: 15,
        max: 135,
        average: 65,
        percentile: calculatePercentile(sinceraData.avg_ad_refresh, 65, 25),
      },
    },
    {
      name: 'Average Ads in View',
      description: 'Number of ads simultaneously visible to users',
      value: sinceraData.avg_ads_in_view ? parseFloat(sinceraData.avg_ads_in_view.toFixed(1)) : 0,
      unit: 'ads',
      optimalRange: { min: 1, max: 2 },
      distributionData: generateNormalDistribution(1.5, 0.8, 0, 6),
      benchmarkStats: {
        min: 0,
        max: 5,
        average: 1.5,
        percentile: calculatePercentile(sinceraData.avg_ads_in_view || 0, 1.5, 0.8),
      },
    },
    {
      name: 'Page Weight',
      description: 'Total size of page resources (lower is better for performance)',
      value: parseFloat(sinceraData.avg_page_weight.toFixed(1)),
      unit: 'MB',
      optimalRange: { min: 2, max: 8 },
      distributionData: generateNormalDistribution(12, 6, 0, 40),
      benchmarkStats: {
        min: 2.1,
        max: 35.7,
        average: 12,
        percentile: 100 - calculatePercentile(sinceraData.avg_page_weight, 12, 6), // Invert since lower is better
      },
    },
    {
      name: 'CPU Usage',
      description: 'Processing power consumed by ads (lower is better)',
      value: parseFloat(sinceraData.avg_cpu.toFixed(1)),
      unit: '%',
      optimalRange: { min: 10, max: 30 },
      distributionData: generateNormalDistribution(35, 15, 0, 100),
      benchmarkStats: {
        min: 15,
        max: 85,
        average: 35,
        percentile: 100 - calculatePercentile(sinceraData.avg_cpu, 35, 15), // Invert since lower is better
      },
    },
  ];

  const getKeySignals = () => {
    const signals: Array<{
      metric: string;
      status: 'positive' | 'negative';
      message: string;
    }> = [];
    
    metricsData.forEach(metric => {
      const percentile = metric.benchmarkStats.percentile;
      const isOptimal = metric.value >= metric.optimalRange.min && metric.value <= metric.optimalRange.max;
      
      if (percentile > 85 || isOptimal) {
        signals.push({
          metric: metric.name,
          status: 'positive',
          message: `${sinceraData.name}'s ${metric.name} is ${isOptimal ? 'in the optimal range' : `better than ${percentile}% of publishers`}`,
        });
      } else if (percentile < 25) {
        signals.push({
          metric: metric.name,
          status: 'negative',
          message: `${sinceraData.name}'s ${metric.name} is below industry standards`,
        });
      }
    });
    
    return signals;
  };

  const keySignals = getKeySignals();

  return (
    <div className="benchmark-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h2>📊 Key Signals</h2>
          <p className="header-subtitle">
            {sinceraData.name}'s key signal data, graphed.
          </p>
        </div>
        <div className="distribution-toggle">
          <button className="toggle-btn active">Normal Distribution</button>
          <button className="toggle-btn">Standard Distribution</button>
        </div>
      </div>

      {keySignals.length > 0 && (
        <div className="key-signals-summary">
          {keySignals.map((signal, index) => (
            <div key={index} className={`signal-item ${signal.status}`}>
              <span className="signal-icon">
                {signal.status === 'positive' ? '🟢' : '🔴'}
              </span>
              <span className="signal-text">{signal.message}</span>
            </div>
          ))}
        </div>
      )}

      <div className="metrics-grid">
        {metricsData.slice(0, 4).map((metric, index) => (
          <MetricDistributionCard
            key={metric.name}
            metric={metric}
            isHighlighted={index === 0} // Highlight first metric (A2CR)
          />
        ))}
      </div>
    </div>
  );
};

export default BenchmarkDashboard;